import { Injectable } from '@angular/core';
import { Observable, of, Subject, Subscription, switchMap, timer } from 'rxjs';
import { SocketService, SocketGroup } from './socket.service';
import { catchError, finalize } from 'rxjs/operators';
import { ExcelService } from './xls.service';
import { ISyncProductMessage } from '../../product/models/models';
import { PlanogramsService } from './planograms.service';
import { INotification } from '../models/notifications';
import { NotificationsService } from './notifications.service';
import { ITableExcelData, ITableExcelResponse } from '../models/table';
import * as uuid from 'uuid';

type ExecutionCounter = {
	id: string;
	current: number;
	total: number;
	display: number;
	notificationId?: number | null;
	finishedAt?: string | null;
	equalDownloading: () => boolean;
}

type ExecutionItem = {
	active: boolean;
	items: Array<ExecutionCounter>;
}

type AnalyticsDownloadItem = {
	active: boolean;
	files: Map<string, DownloadAnalyticsFile>;
}

type DownloadAnalyticsFile = {
	subscription: Subscription;
	cancel: boolean;
	complete: boolean;
	name: string;
	id: string;
}

type ProductsUpdateItem = {
	active: boolean;
	items: Array<ProductsUpdateCounter>;
}

type ProductsUpdateCounter = {
	id: number | string;
	name: string;
	current: number;
	total: number;
	equalDownloading: () => boolean;
}

type StandEquipmentsItem = {
	active: boolean;
	items: Array<IStandEquipmentsCounter>;
}

type ComplianceCheckItem = {
	active: boolean;
	items: Array<IComplianceCheckCounter>;
}

export interface IDownloadItems {
	execution: ExecutionItem;
	scheduleExecution: ExecutionItem;
	excelFilesDownload: AnalyticsDownloadItem;
	analyticsDownload: AnalyticsDownloadItem;
	productsUpdate: ProductsUpdateItem;
	equipmentsDownload: StandEquipmentsItem;
	complianceCheck: ComplianceCheckItem;
	someActive: () => boolean;
	showMenu: boolean;
}

export interface IStandEquipmentsCounter {
	name: string;
	current: number;
	total: number;
	file: string;
	format: string;
	equalDownloading: () => boolean;
	id: string;
	date: string;
}

type ComplianceCheckStatus = 'pending' | 'in_progress' | 'failed' | 'finished';

export interface IComplianceCheckCounter {
	id: string;
	name: string;
	current: number;
	total: number;
	status: ComplianceCheckStatus;
	statusText: string;
	planogramId: number;
	planId: number;
	mainPlanogramId: number;
	photoreportId: number;
}

type JobItem = {
	id: string;
	type: string;
	name: string;
	progress?: {
		current: number;
		total: number;
	};
	status?: string;
	config?: any;
	started_at?: string;
	finished_at?: string | null;
	url?: string | null;
	file_extension?: string;
}


type AnalyticsDownloadSubject = {
	name: string;
	getTableData$: Observable<ITableExcelResponse>;
	sheet_names: Array<string>;
	image: string;
}

type ExcelFilesDownloadSubject = {
	name: string;
	getData$: Observable<void>;
	params?: any;
}

const DOWNLOAD_AFTER_DELAY: number = 5e3; // 5 seconds
const EXECUTION_AUTO_DELETE_DELAY: number = 5 * 60 * 1000;

@Injectable({
	providedIn: 'root'
})
export class DownloadService {
	analyticsDownloadSubject: Subject<AnalyticsDownloadSubject> = new Subject<AnalyticsDownloadSubject>();
	excelFilesDownloadSubject: Subject<ExcelFilesDownloadSubject> = new Subject<ExcelFilesDownloadSubject>();
	downloadList: IDownloadItems;
	private equipmentRemovalTimers: Map<string, Subscription> = new Map<string, Subscription>();
	private complianceRemovalTimers: Map<string, Subscription> = new Map<string, Subscription>();
	private executionRemovalTimers: Map<string, Subscription> = new Map<string, Subscription>();
	openNotificationSubject: Subject<INotification> = new Subject<INotification>();

	constructor(
		private socketService: SocketService,
		private excelService: ExcelService,
		private standsService: PlanogramsService,
		private notificationsService: NotificationsService
	) {}

	private deleteStandEquipmentsFromArray(id: string): void {
		if (this.equipmentRemovalTimers.has(id)) {
			this.equipmentRemovalTimers.get(id).unsubscribe();
			this.equipmentRemovalTimers.delete(id);
		}
		this.downloadList.equipmentsDownload.items = this.downloadList.equipmentsDownload.items
			.filter((equipment: IStandEquipmentsCounter) => equipment.id !== id);
		this.downloadList.equipmentsDownload.active = !!this.downloadList.equipmentsDownload.items.length;
		this.downloadList.showMenu = this.downloadList.someActive();
	}

	deleteComplianceCheckFromArray(id: string): void {
		if (this.complianceRemovalTimers.has(id)) {
			this.complianceRemovalTimers.get(id).unsubscribe();
			this.complianceRemovalTimers.delete(id);
		}
		this.downloadList.complianceCheck.items = this.downloadList.complianceCheck.items
			.filter((item: IComplianceCheckCounter) => item.id !== id);
		this.downloadList.complianceCheck.active = !!this.downloadList.complianceCheck.items.length;
		this.downloadList.showMenu = this.downloadList.someActive();
	}

	private setAnalyticsDownload(): void {
		this.analyticsDownloadSubject.subscribe((params: AnalyticsDownloadSubject) => {
			this.downloadList.analyticsDownload.active = true;
			this.downloadList.showMenu = this.downloadList.someActive();

			const id: string = String(uuid.v4());

			const request$: Observable<void> = params.getTableData$.pipe(
				switchMap((response: ITableExcelResponse) => {
					const excelParams: ITableExcelData = {
						rows: response.data,
						sheet_names: params.sheet_names,
						column_titles: response.column_titles,
						image: params.image
					};
					return this.excelService.exportComplexAnalytics(excelParams, params.name)
				})
			);

			this.downloadList.analyticsDownload.files.set(id, {
				subscription: request$
					.pipe(
						finalize(() => {
							const file: DownloadAnalyticsFile = this.downloadList.analyticsDownload.files.get(id);
							if (file.cancel) {
								this.downloadList.analyticsDownload.files.delete(id);
								this.downloadList.analyticsDownload.active = !!this.downloadList.analyticsDownload.files.size;
								this.downloadList.showMenu = this.downloadList.someActive();
							} else {
								file.complete = true;
								timer(DOWNLOAD_AFTER_DELAY).subscribe(() => {
									this.downloadList.analyticsDownload.files.delete(id);
									this.downloadList.analyticsDownload.active = !!this.downloadList.analyticsDownload.files.size;
									this.downloadList.showMenu = this.downloadList.someActive();
								});
							}
						})
					)
					.subscribe(),
				id: id,
				cancel: false,
				complete: false,
				name: params.name
			});
		});
	}

	private setEquipmentsDownload(): void {
		this.socketService.addSubscription(SocketGroup.Jobs);

		this.standsService.getJobs()
			.pipe(catchError(() => of([])))
			.subscribe((response: Array<JobItem>) => {
				response.forEach((job: JobItem) => {
					switch (job.type) {
						case 'equipment_download':
							this.updateStandEquipment(job);
							break;
						case 'execution_send':
							this.updateExecutionJob(job);
							break;
						case 'schedule_execution_send':
							this.updateExecutionJob(job, true);
							break;
						case 'planogram_compliance_check':
							this.updateComplianceCheck(job);
							break;
					}
				});
			});


		this.socketService.jobsUpdate.subscribe((job: JobItem) => {
			switch (job.type) {
				case 'equipment_download': {
					const item = this.updateStandEquipment(job);
					if (item.equalDownloading() && item.file && document.hasFocus()) {
						this.downloadStandEquipments(item);
					}
					break;
				}
				case 'execution_send':
					this.updateExecutionJob(job);
					break;
				case 'schedule_execution_send':
					this.updateExecutionJob(job, true);
					break;
				case 'planogram_compliance_check':
					this.updateComplianceCheck(job);
					break;
			}
		});

		this.socketService.jobsDelete.subscribe((payload: { job_id: string }) => {
			const id = payload.job_id;
			if (this.downloadList.equipmentsDownload.items.find((item: IStandEquipmentsCounter) => item.id === id)) {
				this.deleteStandEquipmentsFromArray(id);
			}
			if (this.downloadList.complianceCheck.items.find((item: IComplianceCheckCounter) => item.id === id)) {
				this.deleteComplianceCheckFromArray(id);
			}
			this.deleteExecutionById(id, false);
			this.deleteExecutionById(id, true);
		});
	}

	private setExcelFilesDownload(): void {
		this.excelFilesDownloadSubject.subscribe(async (params: ExcelFilesDownloadSubject) => {
			this.downloadList.excelFilesDownload.active = true;
			this.downloadList.showMenu = this.downloadList.someActive();

			const id: string = String(uuid.v4());
			const request$: Observable<void> = params.getData$;

			this.downloadList.excelFilesDownload.files.set(id, {
				subscription: request$
					.pipe(
						finalize(() => {
							const file: DownloadAnalyticsFile = this.downloadList.excelFilesDownload.files.get(id);
							if (file.cancel) {
								this.downloadList.excelFilesDownload.files.delete(id);
								this.downloadList.excelFilesDownload.active = !!this.downloadList.excelFilesDownload.files.size;
								this.downloadList.showMenu = this.downloadList.someActive();
							} else {
								file.complete = true;
								timer(DOWNLOAD_AFTER_DELAY).subscribe(() => {
									this.downloadList.excelFilesDownload.files.delete(id);
									this.downloadList.excelFilesDownload.active = !!this.downloadList.excelFilesDownload.files.size;
									this.downloadList.showMenu = this.downloadList.someActive();
								});
							}
						})
					)
					.subscribe(),
				id: id,
				cancel: false,
				complete: false,
				name: params.name
			});
		});
	}

	private setExecution(): void {
		this.socketService.notification.subscribe((notification: INotification) => {
			if (notification.type === 'multi_execution') {
				const execution = this.downloadList.execution.items.find(item => item.notificationId === notification.id);
				if (execution) {
					execution.notificationId = notification.id;
				}
			}
		});
	}

	private setScheduleExecution(): void {
		this.socketService.notification.subscribe((notification: INotification) => {
			if (notification.type === 'scheduled_execution') {
				const execution = this.downloadList.scheduleExecution.items.find(item => item.notificationId === notification.id);
				if (execution) {
					execution.notificationId = notification.id;
				}
			}
		});
	}

	private setProductsUpdate(): void {
		this.socketService.productSyncStart.subscribe((data: ISyncProductMessage) => {
			this.updateProductsData(data);
			this.downloadList.productsUpdate.active = true;
			this.downloadList.showMenu = this.downloadList.someActive();
		});
		this.socketService.productSyncFinish.subscribe((data: ISyncProductMessage) => {
			const update = this.updateProductsData(data);

			if (update.equalDownloading()) {
				timer(DOWNLOAD_AFTER_DELAY).subscribe(() => {
					this.downloadList.productsUpdate.items = this.downloadList.productsUpdate.items.filter(item => item !== update);
					this.downloadList.productsUpdate.active = !!this.downloadList.productsUpdate.items.length;
					this.downloadList.showMenu = this.downloadList.someActive();
				});
			} else {
				this.downloadList.productsUpdate.active = true;
				this.downloadList.showMenu = this.downloadList.someActive();
			}
		});
	}

	private updateProductsData(data: ISyncProductMessage): ProductsUpdateCounter {
		let update: ProductsUpdateCounter = this.downloadList.productsUpdate.items.find(update => update.id === data.user_id);

		if (update) {
			update.current = data.current;
			update.total = data.total;
		} else {
			update = {
				id: data.user_id,
				name: data.full_name,
				current: data.current,
				total: data.total,
				equalDownloading(): boolean {
					return this.current === this.total;
				}
			};
			this.downloadList.productsUpdate.items.push(update);
		}

		return update;
	}

	private updateStandEquipment(data: JobItem): IStandEquipmentsCounter {
		this.downloadList.equipmentsDownload.active = true;
		this.downloadList.showMenu = this.downloadList.someActive();

		const id: string = data.id;
		const current: number = data.progress?.current ?? 0;
		const total: number = data.progress?.total ?? 0;
		const url: string | null = data.url ?? data.config?.url ?? null;
		const rawExt = data.file_extension ?? data.config?.file_format ?? '';
		const fileExtension: string = rawExt ? '.' + rawExt : '';
		const startedAt: string = data.started_at ?? new Date().toISOString();
		const finishedAt: string | null = data.finished_at ?? null;
		const allReadyEquipment: IStandEquipmentsCounter = this.downloadList.equipmentsDownload.items
			.find((item: IStandEquipmentsCounter) => item.id === id);

		if (allReadyEquipment) {
			allReadyEquipment.current = current;
			allReadyEquipment.total = total;
			if (allReadyEquipment.equalDownloading())
				allReadyEquipment.file = url;
			this.scheduleEquipmentRemoval(id, finishedAt);
			return allReadyEquipment;
		}

		const item: IStandEquipmentsCounter = {
			id,
			name: data.name,
			current,
			total,
			format: fileExtension,
			file: url ?? '',
			date: startedAt,
			equalDownloading(): boolean {
				return this.total === this.current;
			}
		};
		this.downloadList.equipmentsDownload.items.push(item);
		this.scheduleEquipmentRemoval(id, finishedAt);
		return item;
	}

	private updateComplianceCheck(data: JobItem): IComplianceCheckCounter {
		this.downloadList.complianceCheck.active = true;
		this.downloadList.showMenu = this.downloadList.someActive();

		const id: string = data.id;
		const current: number = data.progress?.current ?? 0;
		const total: number = data.progress?.total ?? 0;
		const statusText: string = data.config?.message ?? '';
		const status: ComplianceCheckStatus = data.status as ComplianceCheckStatus;
		const finishedAt: string | null = data.finished_at ?? null;
		const existItem: IComplianceCheckCounter = this.downloadList.complianceCheck.items.find((item: IComplianceCheckCounter) => item.id === id);

		if (existItem) {
			existItem.current = current;
			existItem.total = total;
			existItem.statusText = statusText;
			existItem.status = status;
			this.scheduleComplianceRemoval(id, finishedAt);
			return existItem;
		}

		const item: IComplianceCheckCounter = {
			id: id,
			name: data.name,
			current: current,
			total: total,
			status: status,
			statusText: statusText,
			planId: data.config.plan_id,
			planogramId: data.config.planogram_id,
			photoreportId: data.config.photoreport_id,
			mainPlanogramId: data.config.main_planogram_id
		};
		this.downloadList.complianceCheck.items.push(item);
		this.scheduleComplianceRemoval(id, finishedAt);
		return item;
	}

	private updateExecutionJob(job: JobItem, isSchedule: boolean = false): void {
		const target = isSchedule ? this.downloadList.scheduleExecution : this.downloadList.execution;
		const displayValue =
			job.config?.display_total ??
			job.config?.display_total_executions ??
			job.config?.total_executions ??
			job.progress?.total ??
			0;
		
		let execution: ExecutionCounter = target.items.find(item => item.id === job.id);
		
		if (execution) {
			execution.current = job.progress?.current ?? 0;
			execution.total = job.progress?.total ?? 0;
			execution.display = displayValue;
			if (job.config?.notification_id) execution.notificationId = job.config.notification_id;
			if (job.finished_at !== undefined) execution.finishedAt = job.finished_at;
		} else {
			execution = {
				id: job.id,
				current: job.progress?.current ?? 0,
				total: job.progress?.total ?? 0,
				display: displayValue,
				notificationId: job.config?.notification_id ?? null,
				finishedAt: job.finished_at ?? null,
				equalDownloading(): boolean {
					return this.current === this.total;
				}
			};
			target.items.push(execution);
		}
		
		target.active = true;
		this.downloadList.showMenu = this.downloadList.someActive();

		this.scheduleExecutionRemoval(job.id, job.finished_at ?? null, isSchedule);
	}

	cancelAnalyticsDownload(id: string): void {
		this.downloadList.analyticsDownload.files.get(id).cancel = true;
		this.downloadList.analyticsDownload.files.get(id).subscription.unsubscribe();
		this.downloadList.analyticsDownload.files.delete(id);
	}

	cancelExcelFileDownload(id: string): void {
		this.downloadList.excelFilesDownload.files.get(id).cancel = true;
		this.downloadList.excelFilesDownload.files.get(id).subscription.unsubscribe();
		this.downloadList.excelFilesDownload.files.delete(id);
	}

	deleteStandEquipments(id: string): void {
		this.standsService.deleteJob(id).subscribe();
		this.deleteStandEquipmentsFromArray(id);
	}

	private deleteExecutionById(id: string, isSchedule: boolean): void {
		const target = isSchedule ? this.downloadList.scheduleExecution : this.downloadList.execution;
		const execution = target.items.find(item => item.id === id);
		if (execution) {
			if (this.executionRemovalTimers.has(id)) {
				this.executionRemovalTimers.get(id).unsubscribe();
				this.executionRemovalTimers.delete(id);
			}
			target.items = target.items.filter(item => item.id !== id);
			target.active = !!target.items.length;
			this.downloadList.showMenu = this.downloadList.someActive();
		}
	}

	private clearExecutionState(isSchedule: boolean = false): void {
		const target = isSchedule ? this.downloadList.scheduleExecution : this.downloadList.execution;
		target.items.forEach(item => {
			if (this.executionRemovalTimers.has(item.id)) {
				this.executionRemovalTimers.get(item.id).unsubscribe();
				this.executionRemovalTimers.delete(item.id);
			}
		});
		target.active = false;
		target.items = [];
		this.downloadList.showMenu = this.downloadList.someActive();
	}

	private scheduleComplianceRemoval(id: string, finishedAt: string | null): void {
		if (!finishedAt) return;
		const finishTime = new Date(finishedAt).getTime();
		const now = Date.now();
		const remaining = DOWNLOAD_AFTER_DELAY - (now - finishTime);
		if (remaining <= 0) {
			const item = this.downloadList.complianceCheck.items.find(item => item.id === id);
			if (item.status !== 'finished') return;
			this.deleteComplianceCheckFromArray(id);
			return;
		}
		if (this.complianceRemovalTimers.has(id)) {
			this.complianceRemovalTimers.get(id).unsubscribe();
			this.complianceRemovalTimers.delete(id);
		}
		const sub = timer(remaining).subscribe(() => {
			const item = this.downloadList.complianceCheck.items.find(item => item.id === id);
			if (item.status !== 'finished') return;
			this.deleteComplianceCheckFromArray(id);
		});
		this.complianceRemovalTimers.set(id, sub);
	}

	private scheduleEquipmentRemoval(id: string, finishedAt: string | null): void {
		if (!finishedAt) return;
		const finishTime = new Date(finishedAt).getTime();
		const now = Date.now();
		const remaining = EXECUTION_AUTO_DELETE_DELAY - (now - finishTime);
		if (remaining <= 0) {
			const equipment = this.downloadList.equipmentsDownload.items.find(item => item.id === id);
			if (equipment?.file || !document.hasFocus()) return;
			this.deleteStandEquipmentsFromArray(id);
			return;
		}
		if (this.equipmentRemovalTimers.has(id)) {
			this.equipmentRemovalTimers.get(id).unsubscribe();
			this.equipmentRemovalTimers.delete(id);
		}
		const sub = timer(remaining).subscribe(() => {
			const equipment = this.downloadList.equipmentsDownload.items.find(item => item.id === id);
			if (equipment?.file || !document.hasFocus()) return;
			this.deleteStandEquipmentsFromArray(id);
		});
		this.equipmentRemovalTimers.set(id, sub);
	}

	private scheduleExecutionRemoval(id: string, finishedAt: string | null, isSchedule: boolean): void {
		if (!finishedAt) return;
		const finishTime = new Date(finishedAt).getTime();
		const now = Date.now();
		const remaining = EXECUTION_AUTO_DELETE_DELAY - (now - finishTime);
		if (remaining <= 0) {
			this.deleteExecutionById(id, isSchedule);
			return;
		}
		if (this.executionRemovalTimers.has(id)) {
			this.executionRemovalTimers.get(id).unsubscribe();
			this.executionRemovalTimers.delete(id);
		}
		const sub = timer(remaining).subscribe(() => {
			this.deleteExecutionById(id, isSchedule);
			this.executionRemovalTimers.delete(id);
		});
		this.executionRemovalTimers.set(id, sub);
	}

	downloadStandEquipments(item: IStandEquipmentsCounter): void {
		if (!item.file || !document.hasFocus()) return;
		const target: string = item.format === '.pdf' ? '_blank' : '_self';
		window.open(item.file, target);
		this.deleteStandEquipments(item.id);
	}

	deleteExecution(id?: string): void {
		if (id) {
			this.standsService.deleteJob(id).subscribe();
			this.deleteExecutionById(id, false);
		} else {
			this.downloadList.execution.items.forEach(item => {
				this.standsService.deleteJob(item.id).subscribe();
			});
			this.clearExecutionState(false);
		}
	}

	deleteScheduleExecution(id?: string): void {
		if (id) {
			this.standsService.deleteJob(id).subscribe();
			this.deleteExecutionById(id, true);
		} else {
			this.downloadList.scheduleExecution.items.forEach(item => {
				this.standsService.deleteJob(item.id).subscribe();
			});
			this.clearExecutionState(true);
		}
	}

	openExecutionNotification(executionId?: string): void {
		const execution = executionId 
			? this.downloadList.execution.items.find(item => item.id === executionId)
			: this.downloadList.execution.items.find(item => item.notificationId);
		if (!execution || !execution.notificationId) return;
		this.notificationsService.get(execution.notificationId).subscribe({
			next: (notification: INotification) => {
				this.openNotificationSubject.next(notification);
				this.deleteExecution(execution.id);
			}
		});
	}

	openScheduleExecutionNotification(executionId?: string): void {
		const execution = executionId 
			? this.downloadList.scheduleExecution.items.find(item => item.id === executionId)
			: this.downloadList.scheduleExecution.items.find(item => item.notificationId);
		if (!execution || !execution.notificationId) return;
		this.notificationsService.get(execution.notificationId).subscribe({
			next: (notification: INotification) => {
				this.openNotificationSubject.next(notification);
				this.deleteScheduleExecution(execution.id);
			}
		});
	}

	onInit(): void {
		this.downloadList = {
			execution: {
				active: false,
				items: []
			},
			scheduleExecution: {
				active: false,
				items: []
			},
			excelFilesDownload: {
				active: false,
				files: new Map<string, DownloadAnalyticsFile>()
			},
			analyticsDownload: {
				active: false,
				files: new Map<string, DownloadAnalyticsFile>()
			},
			productsUpdate: {
				active: false,
				items: []
			},
			equipmentsDownload: {
				active: false,
				items: []
			},
			complianceCheck: {
				active: false,
				items: []
			},
			someActive(): boolean {
				return this.execution.active ||
					this.scheduleExecution.active ||
					this.productsUpdate.active ||
					this.equipmentsDownload.active ||
					this.analyticsDownload.active ||
					this.complianceCheck.active ||
					this.excelFilesDownload.active;
			},
			showMenu: false
		};
		this.setExecution();
		this.setScheduleExecution();
		this.setExcelFilesDownload();
		this.setProductsUpdate();
		this.setEquipmentsDownload();
		this.setAnalyticsDownload();
		this.setWindowFocusHandler();
	}

	private setWindowFocusHandler(): void {
		window.addEventListener('focus', () => {
			this.checkAndRemoveFinishedExecutions(false);
			this.checkAndRemoveFinishedExecutions(true);
		});
	}

	private checkAndRemoveFinishedExecutions(isSchedule: boolean): void {
		const target = isSchedule ? this.downloadList.scheduleExecution : this.downloadList.execution;
		const now = Date.now();
		target.items.forEach(execution => {
			if (execution.equalDownloading() && execution.finishedAt) {
				const elapsed = now - new Date(execution.finishedAt).getTime();
				if (elapsed >= EXECUTION_AUTO_DELETE_DELAY) {
					this.deleteExecutionById(execution.id, isSchedule);
				}
			}
		});
	}
}
